import * as Sentry from '@sentry/react';
import { deleteCookie } from '@do/seashell';
import { useMutation } from '@do/graphql-client';
import { useNavigate } from '@do/seashell/navigation';
import { Button, Text as WalrusText, Tooltip, style, Box } from '@do/walrus';
import { Form, Formik } from 'formik';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { PreferredLoginProps, LoginStep, isLoginStep } from '../../interfaces/login';
import { LoginVariantProps } from '../../interfaces/loginVariant';
import vars from '../../assets/overwrite-vars';
import successIcon from '../../assets/success-icon.svg';
import { FORCE_EXTERNAL, LOGIN_ENDPOINT, PREFERRED_SIGN_IN_COOKIE } from '../../constants';
import copy from '../../copy';
import { loginAccount as LoginAccount, loginAccountVariables } from '../../generated-types/loginAccount';
import { LOG_IN_ACCOUNT } from '../../queries/queries';
import { addSessionData, handleFormError, saveSessionData } from '../../utils/gql-utils';
import { replaceInterpolatedVariables } from '../../utils/string-utils';
import ErrorBanner from '../ErrorBanner';
import GithubButton from '../GithubButton';
import GoogleButton from '../GoogleButton';
import Link from '../Link';
import SpinnerButton from '../SpinnerButton';
import SuccessBanner from '../SuccessBanner';
import Text from '../Text';
import TextInput from '../TextInput';
import Title from '../Title';
import Wrapper from '../Wrapper';
import { registrationTrackingIds } from '../../tracking-ids';
import { teamVariant } from '../../login-variants';
import DOButton from '../DOButton';
import { ButtonStyles } from '../RegistrationTemplateProvider';

interface AvatarProps {
  src: string;
}

const NewFormHr = styled.hr`
  height: ${style.vars.borderWidth.thin};
  margin-bottom: 0;
  margin-top: ${style.vars.whitespace.medium};
  color: ${vars.grey7};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  border-radius: ${style.vars.borderRadius.base};
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.primary};
  margin-bottom: ${style.vars.space['3']};
  padding: ${style.vars.space['3']};
`;

const NewUserInfo = styled(UserInfo)`
  margin: ${style.vars.space['4']} 0;
  padding: ${style.vars.space['4']};
  border-radius: ${vars.border_radius_xlarge};
  border-color: ${vars.grey6};
`;

const Avatar = styled.img<AvatarProps>`
  width: ${style.vars.space['9']};
  height: ${style.vars.space['9']};
  border-radius: 50%;
  margin-right: ${style.vars.space['4']};
`;

const Email = styled(WalrusText)`
  word-break: break-word;
`;

const ForgotPassword = styled.div`
  margin: ${style.vars.space['3']} 0 ${style.vars.space['2']};
  text-align: center;

  &:not(:last-child) {
    margin-top: ${style.vars.space['4']};
  }
`;

const Preamble = styled.span`
  color: ${style.colors.black};
  margin-right: ${style.vars.space['2']};
  font-size: ${style.vars.fontSize.small};
  line-height: 28px;
`;

const SignUpTopHr = styled.hr`
  margin-bottom: ${style.vars.whitespace.medium};
  margin-top: ${style.vars.whitespace.small};
  color: ${vars.grey7};
  border-width: 2px;
`;

const ForgetPreferredSignInWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`;

const FormValidation = Yup.object().shape({
  email: Yup.string().email(copy.form.validation.email_valid).required(copy.form.validation.email_required),
  password: Yup.string().required(copy.form.validation.password_required),
});

const PreferredEmailValidation = Yup.object().shape({
  password: Yup.string().required(copy.form.validation.password_required),
});

interface Props {
  githubOauthUrl: string;
  googleOauthUrl: string;
  doOauthurl: string;
  passwordSuccessBanner?: 'forgot' | 'reset';
  showNext: (page: LoginStep) => void;
  user?: PreferredLoginProps;
  hasPreferredLogin: boolean;
  team:
    | {
        name: string;
      }
    | null
    | undefined;
  variant: LoginVariantProps;
  allowedAuthMethods: string[];
  buttonStyles: ButtonStyles;
  inputLabelColor: string;
}

interface GenericError {
  title: string;
  details: string;
  requestId?: string;
}

interface FormValues {
  email: string;
  password: string;
}

const LoginForm: React.FC<Props> = ({
  githubOauthUrl,
  googleOauthUrl,
  passwordSuccessBanner,
  showNext,
  user,
  hasPreferredLogin,
  team,
  variant,
  allowedAuthMethods,
  doOauthurl,
  buttonStyles,
  inputLabelColor,
}): ReactElement => {
  const [genericServerError, setGenericServerError] = React.useState<GenericError | null>(null);
  const [loginAccount, { loading: isLoading }] = useMutation<LoginAccount, loginAccountVariables>(LOG_IN_ACCOUNT, {
    context: {
      url: LOGIN_ENDPOINT,
    },
  });

  const navigate = useNavigate();

  const userEmail = user?.email;
  const hasPreferredEmail = user?.sign_in_method === 'email';
  const hasPreferredGoogle = user?.sign_in_method === 'google';
  const hasPreferredGithub = user?.sign_in_method === 'github';
  const allowedAuthGoogle = allowedAuthMethods.includes('google');
  const allowedAuthDO = allowedAuthMethods.includes('digitalocean');
  const allowedAuthGithub = allowedAuthMethods.includes('github');
  const allowedAuthEmail = allowedAuthMethods.includes('email');

  const joinTeamCopy = replaceInterpolatedVariables(team || {}, teamVariant);

  const TEAM_INVITE_COOKIE = 'invite_code';

  const initialValues: FormValues = {
    email: (team ? userEmail : '') || '',
    password: '',
  };

  return (
    <Wrapper>
      <Formik<FormValues>
        validateOnBlur={false}
        initialValues={initialValues}
        validationSchema={hasPreferredEmail ? PreferredEmailValidation : FormValidation}
        onSubmit={async (values, { setErrors }) => {
          setGenericServerError(null); /* Reset displayed error */

          return loginAccount({
            variables: {
              loginRequest: addSessionData(values),
            },
          })
            .then((successResponse) => {
              const {
                status,
                session_data: sessionData,
                required_next_step: requiredNextStep,
              } = successResponse?.data?.loginAccount ?? { redirect_url: null };
              const requestId = successResponse.data?.request_id;
              saveSessionData(sessionData);

              if (status === 'granted') {
                window.location.reload();
              } else if (status === 'pending' && isLoginStep(requiredNextStep)) {
                showNext(requiredNextStep);
              } else if (status === 'rejected') {
                setGenericServerError({
                  title: copy.errors.incorrect_email_or_password.title,
                  details: copy.errors.incorrect_email_or_password.details,
                  requestId,
                });
              } else {
                throw new Error(`Unknown login state ${status} - Request ID: ${requestId}`);
              }
            }, handleFormError(setErrors, setGenericServerError))
            .catch((err) => {
              Sentry.captureException(err);
              console.error('Error occurred:', err);
              const requestId = (err as { extensions?: { request_id?: string } })?.extensions?.request_id;
              setGenericServerError({
                title: copy.errors.unknown.title,
                details: copy.errors.unknown.details,
                requestId,
              });
            });
        }}
      >
        {({ errors, values, touched, isSubmitting }) => (
          <>
            {passwordSuccessBanner && (
              <SuccessBanner>
                <img src={successIcon} alt="" />
                {copy.form.login.after_password[passwordSuccessBanner]}
              </SuccessBanner>
            )}

            <Title title={team ? joinTeamCopy.loginTitle : variant.loginTitle} />

            {team && <Text>{joinTeamCopy.loginSubtitle}</Text>}

            {hasPreferredLogin && (
              <>
                <Text>
                  {'preferredLoginSubtitle' in variant ? variant.preferredLoginSubtitle : variant.loginSubtitle}
                </Text>
                <NewUserInfo>
                  <Avatar src={user?.avatar_url || ''} alt="avatar" />
                  <Email>{userEmail}</Email>
                  <ForgetPreferredSignInWrapper>
                    <Tooltip content="forget this sign-in" placement="left">
                      {
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                        <span tabIndex={0}>
                          {/* The above span is necessary for the Tooltip component. See CENG-1691 for more. */}
                          <Button
                            data-tracking-id={registrationTrackingIds.loginForm.forgetPreferredSignIn}
                            variation="icon"
                            icon="close"
                            onClick={() => {
                              // Delete remember me cookie
                              deleteCookie(PREFERRED_SIGN_IN_COOKIE);

                              // Delete invite_code cookie when choosing to sign in as another user
                              deleteCookie(TEAM_INVITE_COOKIE);

                              // Reload /login with cookie deleted to not display preferred login
                              window.location.reload();
                            }}
                          />
                        </span>
                      }
                    </Tooltip>
                  </ForgetPreferredSignInWrapper>
                </NewUserInfo>
              </>
            )}

            {!hasPreferredLogin && <Text>{variant.loginSubtitle}</Text>}

            {(!hasPreferredLogin || hasPreferredEmail) && allowedAuthEmail && (
              <Form autoComplete="none">
                {genericServerError && <ErrorBanner error={genericServerError} />}

                {hasPreferredLogin ? (
                  <TextInput
                    placeholder={copy.form.login.password_placeholder}
                    required
                    name="password"
                    label={copy.form.password_label}
                    type="password"
                    touched={touched.password}
                    error={errors.password}
                    value={values.password}
                    labelColor={inputLabelColor}
                    autoComplete="none"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  />
                ) : (
                  <>
                    <TextInput
                      placeholder={copy.form.email_placeholder}
                      required
                      name="email"
                      label={copy.form.login.email_label}
                      touched={touched.email}
                      error={errors.email}
                      value={values.email}
                      labelColor={inputLabelColor}
                      autoComplete="none"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                    <TextInput
                      placeholder={copy.form.login.password_placeholder}
                      required
                      name="password"
                      label={copy.form.password_label}
                      type="password"
                      touched={touched.password}
                      error={errors.password}
                      value={values.password}
                      labelColor={inputLabelColor}
                      autoComplete="none"
                    />
                  </>
                )}
                <SpinnerButton
                  buttonStyle={buttonStyles.primary}
                  type="submit"
                  fullWidth={true}
                  disabled={isLoading || isSubmitting}
                  spinning={isLoading || isSubmitting}
                  loadingMessage={copy.loading.login.belowButton}
                  data-tracking-id={registrationTrackingIds.loginForm.loginButton}
                >
                  {copy.form.login.button_text}
                </SpinnerButton>
              </Form>
            )}

            {!hasPreferredLogin && <NewFormHr />}

            {(!hasPreferredLogin || hasPreferredGoogle) && allowedAuthGoogle && (
              <Box display="block" mt={style.vars.whitespace.small}>
                <GoogleButton
                  url={googleOauthUrl}
                  text={copy.form.oauth.login.google_button}
                  onClick={() => window.location.assign(googleOauthUrl)}
                  dataTrackingId={registrationTrackingIds.loginForm.googleButton}
                />
              </Box>
            )}
            {(!hasPreferredLogin || hasPreferredGithub) && allowedAuthGithub && (
              <GithubButton
                buttonStyle={buttonStyles.secondary}
                url={githubOauthUrl}
                text={copy.form.oauth.login.github_button}
                onClick={() => window.location.assign(githubOauthUrl)}
                dataTrackingId={registrationTrackingIds.loginForm.githubButton}
              />
            )}
            {!hasPreferredLogin && allowedAuthDO && (
              <Box display="block" mt={style.vars.whitespace.small}>
                <DOButton
                  buttonStyle={buttonStyles.secondary}
                  url={doOauthurl}
                  text={copy.form.oauth.login.do_button}
                  onClick={() => window.location.assign(doOauthurl)}
                  dataTrackingId={registrationTrackingIds.loginForm.doButton}
                />
              </Box>
            )}

            {(!hasPreferredLogin || hasPreferredEmail) && (
              <ForgotPassword style={{ textAlign: 'left' }}>
                <Link
                  data-tracking-id={registrationTrackingIds.loginForm.forgotPasswordLink}
                  forceExternal={FORCE_EXTERNAL}
                  to="/forgot_password/new"
                  onClick={(e) => {
                    navigate('/forgot_password/new', {
                      state: { email: !hasPreferredLogin && values.email },
                    });
                    e.preventDefault();
                  }}
                >
                  {copy.form.forgot_password}
                </Link>
              </ForgotPassword>
            )}

            {hasPreferredLogin && (
              <Box pt={3} pb={1} display="block">
                <span className="need-help-text">{copy.form.not_you}</span>
                <br />
                <Link
                  data-tracking-id={registrationTrackingIds.loginForm.loginDifferentAccount}
                  to={window.location.pathname + window.location.search}
                  onClick={(e) => {
                    // Delete invite_code cookie when choosing to sign in as another user
                    deleteCookie(TEAM_INVITE_COOKIE);

                    navigate(window.location.pathname + window.location.search, {
                      state: { preferredLoginState: 'none' },
                    });

                    e.preventDefault();

                    // Reload /login with new state to not display preferred login
                    window.location.reload();
                  }}
                >
                  {copy.form.login_different_account}
                </Link>
              </Box>
            )}
            <Box pt={2} pb={2} display="block">
              <SignUpTopHr></SignUpTopHr>
              <div className="do-u-flex do-u-flexMiddle">
                <Preamble>{copy.header.login.preamble}</Preamble>
                <Link to={'/registrations/new'} target="_self" color={'blue'} forceExternal={FORCE_EXTERNAL}>
                  {copy.header.login.link_text}
                </Link>
              </div>
            </Box>
          </>
        )}
      </Formik>
    </Wrapper>
  );
};

export default LoginForm;
